import { Button, CircularProgress, useMediaQuery } from '@mui/material';
import { Formik } from 'formik';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/rootCombinedReducers';
import { IUser } from 'types/interfaces/iUser';
import { DialCodes } from 'web-components-ts';

import { AccountUpdateSchema } from '../../../attrs/formValidation';
import { getDialCodeFromCountryCode, isLoading } from '../../../helpers/utils';
import { resetUserUpdateLoadingState, updateUser } from '../../../redux/user/actions';
import AccountFormFields from './AccountFormFields';
import { ButtonContainer } from './elements';

interface AccountFormProps {
  user: IUser;
}

const AccountForm = ({ user }: AccountFormProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { updateLoadingState } = useSelector((state: RootState) => state.user.user);

  const isSubmitting = isLoading(updateLoadingState.status);

  const isMobile = useMediaQuery('max-width:425px');

  const updateUserInformation = (values: IUser) => {
    dispatch(updateUser(user.id, values));
  };

  // Resets loading/error state
  useLayoutEffect(
    () => () => {
      dispatch(resetUserUpdateLoadingState());
    },
    [dispatch]
  );

  return (
    <Formik
      initialValues={{
        ...user,
        phone: {
          ...user.phone,
          country: ((user || {}).phone || {}).country || DialCodes[0].code
        }
      }}
      validationSchema={AccountUpdateSchema}
      onSubmit={values => updateUserInformation(values)}
      enableReinitialize
    >
      {({ values, dirty, handleReset, handleChange, handleSubmit, handleBlur, errors, touched }) => {
        const selectedDialCode = getDialCodeFromCountryCode(values.phone.country);

        return (
          <form onSubmit={handleSubmit}>
            <AccountFormFields
              user={user}
              dirty={dirty}
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values}
              errors={errors}
              touched={touched}
              selectedDialCode={selectedDialCode}
              updateLoadingState={updateLoadingState}
            />

            <ButtonContainer container>
              <Button
                variant="text"
                color="primary"
                disabled={!dirty || isSubmitting}
                onClick={handleReset}
                fullWidth={isMobile}
              >
                {t('form.cancel')}
              </Button>
              <Button
                disabled={isSubmitting || Object.keys(errors).length !== 0 || !dirty}
                endIcon={isSubmitting && <CircularProgress color="inherit" size={14} style={{ marginLeft: '1rem' }} />}
                variant="contained"
                color="primary"
                type="submit"
                fullWidth={isMobile}
              >
                {t('form.apply')}
              </Button>
            </ButtonContainer>
          </form>
        );
      }}
    </Formik>
  );
};

export default AccountForm;
